import {Component, Input, OnInit} from '@angular/core';
import { StateService, Transition } from '@uirouter/angular';
import { ComplianceItemHelpersService } from '../../services/compliance-item-helpers/compliance-item-helpers.service';
import { EdlItemPacketUserSummaryDTO } from 'src/app/model/item-packet/edl-item-packet-user-summary-dto.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApplicationRoles, EdlEntriesFormBuilderService, EdlEntriesFormGroup, EdlEntry, EdlFeatureFlags, EDLGroupType, Group, PortalSettings, SessionService, User } from '@edl-development/edl-common';
import { EdlItemPacketEntryDTO } from 'src/app/model/item-packet/edl-item-packet-entry.model';
import { UserSummary } from 'src/app/model/user-summary.model';
import { PagingHelpersService } from 'src/app/services/paging-helpers/paging-helpers.service';
import { CplRosterActionsTypes } from 'src/app/shared-compliance-items/enums/cpl-roster-actions-types.enum';
import { EdlItemPacketStatus } from 'src/app/model/enum/item-packet/edl-item-packet-status.enum';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { UserFormNavigation } from 'src/app/users/modify-user/enum/user-form-navigation.enum';
import { InComplianceStatus } from 'src/app/item-packets/common/enums/in-compliance-status.enum';
import { ActionName } from 'src/app/enums/action-name.enum';
import { TimeTrackingTypes } from 'src/app/model/enum/cpl-time-tracking-type.enum';
import { NavigationItemId } from 'src/app/modals/edl-modal-details/navigation-item-id.enum';
import { UserCycleDetailsModal } from 'src/app/supervision-management/user-cycle-details-modal/user-cycle-details-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimeTrackingHelperService } from 'src/app/time-tracking/services/time-tracking-helper/time-tracking-helper.service';
import { CplUserStatus } from 'src/app/supervision-management/compliance-items/compliance-item/enums/cpl-user-status.enum';
import { RecurringItemConfirmationModalComponent, RECURRING_ITEM_MODAL_ACTION, RecurringItemModalAction } from 'src/app/my-compliance-items/compliance-item-cycle/modals/recurring-item-confirmation-modal.component';
import { ComplianceItemService } from 'src/app/services/compliance-item.service';


export const USER_SUMMARY_FILTER_STATUS = EdlItemPacketStatus.PUBLISHED;
     
@Component({
    selector: 'user-summary',
    templateUrl: './user-summary.component.html',
    styleUrls: [
        './user-summary.component.scss',
        '../../supervision-management/compliance-items/compliance-items.scss'
    ]
})
export class UserSummaryComponent implements OnInit {
    @Input() user: User;
    @Input() summaryItems: UserSummary[] = [];
    @Input() isUser: boolean; // True when the logged in user is the same as the user of this summary
    @Input() isMyItems: boolean; // True when viewing the "My Items" navigation page, false when viewing from "All Users' Progress"
    @Input() archived: string;

    @Input() get itemPackets(): EdlItemPacketUserSummaryDTO[] {
        return this._itemPackets;
    }
    set itemPackets(value: EdlItemPacketUserSummaryDTO[]) {
        this._itemPackets = value.filter(i => i.Packet.Status === USER_SUMMARY_FILTER_STATUS);
        this._itemPacketEntries = value.map(i => i.Packet);
        this.compliantItemPackets = this._itemPackets.filter(i => i.OverallStatus === InComplianceStatus.IN_COMPLIANCE);
        this.nonCompliantItemPackets = this._itemPackets.filter(i => i.OverallStatus === InComplianceStatus.MISSING || i.OverallStatus === InComplianceStatus.OUT_OF_COMPLIANCE);
    }
    private _itemPackets: EdlItemPacketUserSummaryDTO[] = [];
    private _itemPacketEntries: EdlItemPacketEntryDTO[] = [];
    compliantItemPackets: EdlItemPacketUserSummaryDTO[] = [];
    nonCompliantItemPackets: EdlItemPacketUserSummaryDTO[] = [];
    get itemPacketEntries(): EdlItemPacketEntryDTO[] {
        return this._itemPacketEntries.filter((entry: EdlItemPacketEntryDTO) => {
            return entry.Status === USER_SUMMARY_FILTER_STATUS;
        });
    }

    today: Date;
    portalSettings: PortalSettings;
    viewStatus: boolean;
    canViewUserProfile: boolean = false;
    form: FormGroup;

    groups: { [key in EDLGroupType]?: Group[] } = {};

    EDLGroupType = EDLGroupType;
    CplRosterActionsTypes = CplRosterActionsTypes;
    EdlFeatureFlags = EdlFeatureFlags;
    UserFormNavigation = UserFormNavigation;
    ActionName = ActionName;
    USER_SUMMARY_FILTER_STATUS = USER_SUMMARY_FILTER_STATUS;
    TimeTrackingType = TimeTrackingTypes;
    NavigationItemId = NavigationItemId;
    ApplicationRoles = ApplicationRoles;

    showReturnToAllUsersProgress: boolean = false;

    constructor(
        public sessionService: SessionService,
        private stateService: StateService,
        private complianceItemHelpersService: ComplianceItemHelpersService,
        private edlEntriesFormBuilderService: EdlEntriesFormBuilderService,
        private pagingHelpersService: PagingHelpersService,
        private formBuilder: FormBuilder,
        private transition: Transition,
        private timeTrackingHelperService: TimeTrackingHelperService,
        private ngbModal: NgbModal,
        private complianceItemService: ComplianceItemService
    ) { }

    ngOnInit() {
        const params = this.transition.params();
        this.today = new Date();
        this.today.setHours(4, 0, 0, 0);
        this.portalSettings = this.sessionService.PortalSettings;
        this.viewStatus = this.user.Archived;
        this.archived = params.archived;

        
        const currentUserId = this.sessionService.user?.UserID;

        this.showReturnToAllUsersProgress = ((params.userId !== null && params.userId !== undefined) && (currentUserId !== params.userId));

        // organize groups by type
        this.user?.UserGroups?.forEach((group) => {
            if (!this.groups[group.Type]) {
                this.groups[group.Type] = [];
            }

            this.groups[group.Type].push(group);
        });

        const itemPackets = this.pagingHelpersService.toArrayParam(params.itemPackets);
        const entries = this.itemPackets.map(i => i.Packet).filter((entry: EdlItemPacketEntryDTO) => {
            return itemPackets.includes(entry.Id);
        }).map((entry: EdlItemPacketEntryDTO) => ({
            id: entry.Id,
            name: entry.Title,
            details: entry
        }));

        const packetsForm = this.edlEntriesFormBuilderService.buildSelectedFormGroup(entries);
        this.form = this.formBuilder.group({
            archived: [this.archived ?? ""],
            filterByItemPacket: packetsForm
        });

        this.form.valueChanges.subscribe(() => {
            this.onSearch(this.form);
        });
    }

    get canViewItemPackets() {
        return this.itemPackets && this.itemPackets.length > 0;
    }

    private onSearch(form: FormGroup) {
        this.sessionService.Loading(true);

        const archived = form.get('archived').value;
        const itemPackets = this.edlEntriesFormBuilderService
            .toSelectedEntries(this.form.get('filterByItemPacket') as EdlEntriesFormGroup<EdlItemPacketEntryDTO>)
            .map((entry: EdlEntry<EdlItemPacketEntryDTO>) => entry.id)

        this.stateService
            .go('.', { page: 1, archived, itemPackets }, { reload: true })
            .finally(() => this.sessionService.Loading(false));
    }

    GetDaysRemaining(summaryItem: UserSummary, days:number){
        return this.complianceItemHelpersService.GetDaysRemaining(summaryItem.UserCycle);
    }

    Negative(summaryItem: UserSummary) {
        return this.complianceItemHelpersService.Negative(summaryItem.UserCycle);

    }

    getFlexHours(summaryItem: UserSummary){
        return this.timeTrackingHelperService.getUserCycleDisplayHours(summaryItem.UserCycle, summaryItem.CplItem);
    }


    getHours(summaryItem: UserSummary){
        return summaryItem.UserCycle.A48Hours > 0 ? summaryItem.UserCycle.A48Hours :  0;
    }

    getActivityType(summaryItem: UserSummary){
        return summaryItem.UserCycle.A48Hours > 0 ? summaryItem.UserCycle.A48ActivityType :  summaryItem.CplItem.Act48Detail.ActivityType;
    }

    openDetailsModal(summaryItem: UserSummary, navigationItemId: NavigationItemId){
        let modalRef = this.ngbModal.open(UserCycleDetailsModal, {
            size: 'lg',
            backdrop: 'static',
            windowClass: 'edl-modal-details'
        });
        modalRef.componentInstance.originalUserCycle = summaryItem.UserCycle;
        modalRef.componentInstance.complianceItem = summaryItem.CplItem;
        modalRef.componentInstance.startingNavigationItemId = navigationItemId;
        modalRef.componentInstance.CanManageItem = this.canManageItem(summaryItem);
        modalRef.componentInstance.isUser = this.isUser;
        return modalRef.result;
    }

    onViewItem(summaryItem: UserSummary) {
        if (this.isMyItems && this.shouldShowRecurringConfirmModal(summaryItem)) {
            this.openRecurringConfirmModal(summaryItem);
        } else {
            this.stateService.go('^.cycle', {
                cycleId: summaryItem.UserCycle.CycleID,
                Archived: this.viewStatus,
                actionRequired: summaryItem.UserCycle.ActionRequired
            });
        }
    }

    private shouldShowRecurringConfirmModal(summaryItem: UserSummary): boolean {
        const item = summaryItem.CplItem;
        const cycle = summaryItem.UserCycle;
        const isRecurring = item.Recurrence != null;
        const hasPreviousCycles = item.UserHasPreviousCycles;
        const isWaitingOnUser = cycle.UserStatus === CplUserStatus.DEFAULT;
        const daysRemaining = this.complianceItemHelpersService.GetDaysRemaining(cycle);
        return isRecurring && hasPreviousCycles && isWaitingOnUser && daysRemaining > 30;
    }

    private openRecurringConfirmModal(summaryItem: UserSummary) {
        const daysRemaining = this.complianceItemHelpersService.GetDaysRemaining(summaryItem.UserCycle);
        const modalRef = this.ngbModal.open(RecurringItemConfirmationModalComponent, {
            backdrop: 'static',
            size: 'lg'
        });
        modalRef.componentInstance.daysRemaining = daysRemaining;

        modalRef.result.then((action: RecurringItemModalAction) => {
            if (action === RECURRING_ITEM_MODAL_ACTION.CURRENT) {
                this.stateService.go('^.cycle', {
                    cycleId: summaryItem.UserCycle.CycleID,
                    Archived: this.viewStatus,
                    actionRequired: summaryItem.UserCycle.ActionRequired
                });
            } else if (action === RECURRING_ITEM_MODAL_ACTION.HISTORY) {
                this.complianceItemService
                    .getUserCycleHistoryByCycle(summaryItem.UserCycle.CycleID, 0, 40, null, null)
                    .subscribe((history: any) => {
                        const previousCycle = (history.Results as any[])
                            .filter(c => c.CycleID !== summaryItem.UserCycle.CycleID)
                            .sort((a, b) => new Date(b.EndDate).getTime() - new Date(a.EndDate).getTime())[0];

                        this.stateService.go('^.cycle', {
                            cycleId: previousCycle ? previousCycle.CycleID : summaryItem.UserCycle.CycleID,
                            Archived: this.viewStatus
                        });
                    });
            }
        }, () => {
            // cancelled — no navigation, no state mutation
        });
    }

    canManageItem(summaryItem: UserSummary) {
        return this.complianceItemHelpersService.canManageItem(summaryItem.CplItem);
    }

    get canAssignItems(){
        return AccessRoleHelper.isUser(this.user.AccessRoleID) // checking if user has at least user access to assign items
            && (AccessRoleHelper.isSupervisor(this.sessionService.user.AccessRoleID) 
            || AccessRoleHelper.isEDLAdmin(this.sessionService.user.AccessRoleID) 
            || AccessRoleHelper.isAdmin(this.sessionService.user.AccessRoleID));
    }

    viewDetails(summaryItem: UserSummary, navigationItemId: NavigationItemId){
        this.openDetailsModal(summaryItem, navigationItemId)
            .then((shouldRefresh: boolean) => {
                if (shouldRefresh) {
                    this.refreshList(); 
                }
            }, () => {
                this.refreshList();
            })
    }

    refreshList(){
        this.sessionService.Loading(true);
        this.stateService.reload();
    }

    private getItemPacket(id:string){
        return this.itemPackets.find((packet => packet.Packet.Id === id));
    }

    public getInComplianceStatus(id:string) {
        const packet = this.getItemPacket(id);
        return packet.OverallStatus;
    }

    clickReturnToAllUsersProgress() {
        this.stateService.go('authenticated.all-users-progress.user-compliance.view', {
            page: 1
        }, { reload: true });
    }
}
